import React, { useEffect, useState } from 'react'
import { HistoryIcon } from 'lucide-react'
import { fetchInboxAssignmentHistory } from '../../api/inboxAssignmentHistory'
import type { InboxAssignee, InboxItemType } from '../../types/inbox'
import { assigneeDisplayName } from '../../lib/inboxPermissions'

interface InboxAssignmentHistoryPanelProps {
  itemType: InboxItemType
  itemId: string
  assignees: InboxAssignee[]
}

export function InboxAssignmentHistoryPanel({
  itemType,
  itemId,
  assignees,
}: InboxAssignmentHistoryPanelProps) {
  const [rows, setRows] = useState<Awaited<ReturnType<typeof fetchInboxAssignmentHistory>>>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)

  const assigneeById = new Map(assignees.map((a) => [a.id, a]))

  useEffect(() => {
    if (!open) return
    setLoading(true)
    fetchInboxAssignmentHistory(itemType, itemId)
      .then(setRows)
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [open, itemType, itemId])

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
      >
        <HistoryIcon className="w-3.5 h-3.5" />
        Assignment history
      </button>
    )
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
          <HistoryIcon className="w-3.5 h-3.5" />
          Assignment history
        </h4>
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:underline">
          Hide
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-gray-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-gray-400">No assignment changes yet.</p>
      ) : (
        <ul className="space-y-1.5 max-h-40 overflow-y-auto">
          {rows.map((row) => {
            const assignee = row.assigned_to_user_id ? assigneeById.get(row.assigned_to_user_id) : null
            const actor = row.assigned_by_user_id ? assigneeById.get(row.assigned_by_user_id) : null
            return (
              <li key={row.id} className="text-[11px] text-gray-600">
                <span className="text-gray-400">
                  {new Date(row.created_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
                {' — '}
                {row.assigned_to_user_id ? (
                  <>
                    Assigned to <span className="font-medium text-gray-800">{assigneeDisplayName(assignee || { email: 'Unknown' })}</span>
                  </>
                ) : (
                  <span className="font-medium text-gray-800">Unassigned</span>
                )}
                {actor && (
                  <span className="text-gray-400"> by {assigneeDisplayName(actor)}</span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}